import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';

// Components
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import Register from './components/Register';
import MRSManagement from './components/MRSManagement';
import FileUpload from './components/FileUpload';
import PatientDataEntry from './components/PatientDataEntry';
import AIChat from './components/AIChat';
import FormTemplates from './components/FormTemplates';
import Analytics from './components/Analytics';
import DoctorProfile from './components/DoctorProfile';
import Navbar from './components/Navbar';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import SuperAdminLayout from './components/SuperAdminLayout';
import SuperAdminSettings from './components/SuperAdminSettings';
import EmailService from './components/EmailService';
import EmailTemplates from './components/EmailTemplates';
import ChangePassword from './components/ChangePassword';
import FooterSettings from './components/FooterSettings';
import AWSS3Settings from './components/AWSS3Settings';
import SetupWizard from './components/SetupWizard';
import Configuration from './components/Configuration';
import PurposeManagement from './components/PurposeManagement';
import VersionFooter from './components/VersionFooter';
import ThemeSettings from './components/ThemeSettings';
import CohortAIChat from './components/CohortAIChat';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import AnalyticsChatPage from './components/analytics/AnalyticsChatPage';
import { ThemeProvider } from './contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = React.createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupStatus, setSetupStatus] = useState(null);
  const [setupLoading, setSetupLoading] = useState(false);

  useEffect(() => {
    // Check for stored user session
    const storedUser = localStorage.getItem('exlva_dare_user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        // Check setup status for non-super-admin users
        if (parsedUser.role !== 'super_admin') {
          checkSetupStatus();
        }
      } catch (error) {
        localStorage.removeItem('exlva_dare_user');
      }
    }
    setLoading(false);
  }, []);

  const checkSetupStatus = async () => {
    setSetupLoading(true);
    try {
      // Get user from localStorage to pass user_id
      const storedUser = localStorage.getItem('exlva_dare_user');
      if (!storedUser) {
        setSetupLoading(false);
        return;
      }
      
      let currentUser;
      try {
        currentUser = JSON.parse(storedUser);
      } catch (e) {
        setSetupLoading(false);
        return;
      }
      
      if (!currentUser?.id) {
        setSetupLoading(false);
        return;
      }
      
      const response = await axios.get(`${API}/setup/status?user_id=${currentUser.id}`);
      setSetupStatus(response.data);
    } catch (error) {
      console.error('Error checking setup status:', error);
      // If 401/403, user might not have permission - default to needing setup
      if (error.response?.status === 401 || error.response?.status === 403) {
        setSetupStatus({ setup_completed: false });
      } else {
        // For other errors, assume setup is incomplete to force wizard
        setSetupStatus({ setup_completed: false });
      }
    } finally {
      setSetupLoading(false);
    }
  };

  const refreshSetupStatus = async () => {
    await checkSetupStatus();
  };

  const login = async (identifier, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { identifier, password });
      const userData = response.data.user;
      const token = response.data.token;
      
      setUser(userData);
      localStorage.setItem('exlva_dare_user', JSON.stringify(userData));
      if (token) {
        localStorage.setItem('exlva_dare_token', token);
      }
      
      // Check setup status for non-super-admin users
      if (userData.role !== 'super_admin') {
        await checkSetupStatus();
      } else {
        setSetupStatus({ setup_completed: true });
      }
      
      return { success: true, user: userData };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      // Do NOT auto-login - user must wait for admin activation
      // Return success but don't set user state
      return { success: true, message: 'Registration successful' };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('exlva_dare_user');
  };

  const isSuperAdmin = () => {
    return user?.role === 'super_admin';
  };

  const needsSetup = () => {
    // Super admins never need setup
    if (user?.role === 'super_admin') return false;
    // If still loading setup status, don't redirect yet
    if (setupLoading) return false;
    // If no user, they don't need setup (need login first)
    if (!user) return false;
    // If setupStatus is null or not completed, user needs setup
    return !setupStatus || !setupStatus.setup_completed;
  };

  const isSetupReady = () => {
    // Returns true when setup check is complete (either completed or needs wizard)
    return !setupLoading && (user?.role === 'super_admin' || setupStatus !== null);
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    isSuperAdmin,
    setupStatus,
    setupLoading,
    needsSetup,
    isSetupReady,
    refreshSetupStatus
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ProtectedRoute component for regular doctors
const ProtectedRoute = ({ children, skipSetupCheck = false, fullBleed = false }) => {
  const { user, loading, isSuperAdmin, needsSetup, setupLoading, isSetupReady } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect super admin to their dashboard
  if (isSuperAdmin()) {
    return <Navigate to="/admin" replace />;
  }

  // Wait for setup status to load before deciding
  if (!skipSetupCheck && setupLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Redirect to setup wizard if setup not completed (unless skipSetupCheck is true)
  if (!skipSetupCheck && needsSetup()) {
    return <Navigate to="/setup" replace />;
  }

  // fullBleed: app-like pages (e.g. analytics chat) fill all space under the
  // navbar with no container max-width or padding, so the main content area is
  // as large as possible. On large screens the shell is locked to the viewport
  // height (internal scrolling); on small screens it flows so the page scrolls.
  if (fullBleed) {
    return (
      <div className="flex flex-col bg-gray-50 min-h-[100dvh] lg:h-[100dvh] lg:overflow-hidden">
        <Navbar />
        <main className="flex-1 min-h-0 flex flex-col">
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="container mx-auto px-4 py-8">
        {children}
      </main>
    </div>
  );
};

// SetupRoute - only for users who haven't completed setup
const SetupRoute = ({ children }) => {
  const { user, loading, isSuperAdmin, setupStatus, setupLoading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Super admins don't need setup
  if (isSuperAdmin()) {
    return <Navigate to="/admin" replace />;
  }

  // Wait for setup status to load
  if (setupLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // If setup is already completed, redirect to dashboard
  if (setupStatus?.setup_completed) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// SuperAdminRoute component - only accessible by super admins
const SuperAdminRoute = ({ children }) => {
  const { user, loading, isSuperAdmin } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect non-super-admin users to regular dashboard
  if (!isSuperAdmin()) {
    return <Navigate to="/" replace />;
  }

  return (
    <SuperAdminLayout>
      {children}
    </SuperAdminLayout>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="App pb-10">
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/setup" element={
                <SetupRoute>
                  <SetupWizard onComplete={() => window.location.href = '/'} />
                </SetupRoute>
              } />
              <Route path="/configuration" element={
                <ProtectedRoute>
                  <Configuration />
                </ProtectedRoute>
              } />
              <Route path="/" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              <Route path="/mrs" element={
                <ProtectedRoute>
                  <MRSManagement />
                </ProtectedRoute>
              } />
              <Route path="/mrs/:mrsId/upload" element={
                <ProtectedRoute>
                  <FileUpload />
                </ProtectedRoute>
              } />
              <Route path="/mrs/:mrsId/patients" element={
                <ProtectedRoute>
                  <PatientDataEntry />
                </ProtectedRoute>
              } />
              <Route path="/mrs/:mrsId/analytics-chat" element={
                <ProtectedRoute>
                  <AnalyticsChatPage />
                </ProtectedRoute>
              } />
              <Route path="/mrs/:mrsId/ai-analysis" element={
                <ProtectedRoute fullBleed>
                  <CohortAIChat />
                </ProtectedRoute>
              } />
              <Route path="/ai-chat" element={
                <ProtectedRoute>
                  <AIChat />
                </ProtectedRoute>
              } />
              <Route path="/templates" element={
                <ProtectedRoute>
                  <FormTemplates />
                </ProtectedRoute>
              } />
              <Route path="/analytics" element={
                <ProtectedRoute>
                  <Analytics />
                </ProtectedRoute>
              } />
              <Route path="/profile" element={
                <ProtectedRoute>
                  <DoctorProfile />
                </ProtectedRoute>
              } />
              {/* Super Admin Routes */}
              <Route path="/admin" element={
                <SuperAdminRoute>
                  <SuperAdminDashboard />
                </SuperAdminRoute>
              } />
              <Route path="/admin/settings" element={
                <SuperAdminRoute>
                  <Navigate to="/admin/settings/email-service" replace />
                </SuperAdminRoute>
              } />
              <Route path="/admin/settings/integrations" element={
                <SuperAdminRoute>
                  <Navigate to="/admin/settings/email-service" replace />
                </SuperAdminRoute>
              } />
              <Route path="/admin/settings/email-service" element={
                <SuperAdminRoute>
                  <EmailService />
                </SuperAdminRoute>
              } />
              <Route path="/admin/settings/email-templates" element={
                <SuperAdminRoute>
                  <EmailTemplates />
                </SuperAdminRoute>
              } />
              <Route path="/admin/settings/change-password" element={
                <SuperAdminRoute>
                  <ChangePassword />
                </SuperAdminRoute>
              } />
              <Route path="/admin/settings/footer" element={
                <SuperAdminRoute>
                  <FooterSettings />
                </SuperAdminRoute>
              } />
              <Route path="/admin/settings/integrations/s3" element={
                <SuperAdminRoute>
                  <AWSS3Settings />
                </SuperAdminRoute>
              } />
              <Route path="/admin/settings/purposes" element={
                <SuperAdminRoute>
                  <PurposeManagement />
                </SuperAdminRoute>
              } />
              <Route path="/admin/settings/theme" element={
                <SuperAdminRoute>
                  <ThemeSettings />
                </SuperAdminRoute>
              } />
            </Routes>
            <VersionFooter />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;